import { useEffect, useMemo } from "react";

import { useReadRelays } from "../../../hooks/use-client-relays";
import useTimelineLoader from "../../../hooks/use-timeline-loader";
import { eventStore } from "../../../services/event-store";
import pool from "../../../services/pool";
import { GAME_KIND } from "../types";
import { parseGameEvent } from "../nostr-events";

export interface PlayerGameStats {
  wins: number;
  losses: number;
  draws: number;
  totalGames: number;
  winRate: number;
  /** Achievement badges earned based on stats */
  achievements: Achievement[];
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
}

const ACHIEVEMENTS: { id: string; name: string; description: string; icon: string; check: (s: PlayerGameStats) => boolean }[] = [
  { id: "first-blood", name: "First Blood", description: "Win your first game", icon: "\u2694\uFE0F", check: (s) => s.wins >= 1 },
  { id: "veteran", name: "Veteran", description: "Win 5 games", icon: "\uD83C\uDFC5", check: (s) => s.wins >= 5 },
  { id: "champion", name: "Champion", description: "Win 10 games", icon: "\uD83C\uDFC6", check: (s) => s.wins >= 10 },
  { id: "warrior", name: "Warrior", description: "Play 10 games", icon: "\u2694\uFE0F", check: (s) => s.totalGames >= 10 },
  { id: "perfectionist", name: "Perfectionist", description: "Win 5 games with no losses", icon: "\uD83D\uDCAF", check: (s) => s.wins >= 5 && s.losses === 0 },
];

/** Load completed games for a pubkey and compute W/L/D stats */
export default function usePlayerGameStats(pubkey?: string): PlayerGameStats {
  const relays = useReadRelays();

  const filters = useMemo(() => {
    if (!pubkey) return undefined;
    // Query games where pubkey is creator OR opponent (p-tagged)
    return [
      { kinds: [GAME_KIND], authors: [pubkey] },
      { kinds: [GAME_KIND], "#p": [pubkey] },
    ];
  }, [pubkey]);

  const { loader, timeline } = useTimelineLoader("player-stats-" + (pubkey ?? "none"), relays, filters);

  useEffect(() => {
    if (!loader) return;
    const sub = loader(-Infinity).subscribe();
    return () => sub.unsubscribe();
  }, [loader]);

  // Persistent subscription
  useEffect(() => {
    if (!pubkey || relays.length === 0) return;
    const sub = pool
      .request(relays, [
        { kinds: [GAME_KIND], authors: [pubkey] },
        { kinds: [GAME_KIND], "#p": [pubkey] },
      ])
      .subscribe((event) => eventStore.add(event));
    return () => sub.unsubscribe();
  }, [pubkey, relays]);

  return useMemo(() => {
    if (!pubkey) return { wins: 0, losses: 0, draws: 0, totalGames: 0, winRate: 0, achievements: [] };

    let wins = 0;
    let losses = 0;
    let draws = 0;

    for (const event of timeline) {
      const game = parseGameEvent(event);
      if (!game || game.status !== "complete") continue;

      const isCreator = game.creator === pubkey;
      const isOpponent = game.opponent === pubkey;
      if (!isCreator && !isOpponent) continue;

      if (game.winner) {
        if (game.winner === pubkey) wins++;
        else losses++;
      } else {
        draws++;
      }
    }

    const totalGames = wins + losses + draws;
    const winRate = totalGames > 0 ? Math.round((wins / totalGames) * 100) : 0;
    const stats: PlayerGameStats = { wins, losses, draws, totalGames, winRate, achievements: [] };
    stats.achievements = ACHIEVEMENTS.filter((a) => a.check(stats)).map(({ id, name, description, icon }) => ({ id, name, description, icon }));
    return stats;
  }, [pubkey, timeline]);
}
